import { useState } from "react";
import { PButton } from "../ui/PButton";
import { useToast } from "../ui/toast";
import { useCreateSavedFilter } from "./api";
import { ConfirmPopup } from "./ConfirmPopup";
import { canApply, withFilters, type AdvancedFilter, type RangeKey } from "./filter-logic";
import { FilterPlayer } from "./FilterPlayer";
import { FilterRange } from "./FilterRange";
import "./filters.css";

// THE FIVE RANGES, IN TEMPLATE ORDER, WITH THE TEMPLATE'S TITLES AND FLOOR / CEILING
const RANGES: Array<{ key: RangeKey; title: string; formatter: string; min: number; max: number }> = [
  { key: "theo", title: "Total Daily Theo", formatter: "$", min: 0, max: 1000 },
  { key: "gains", title: "Total Daily Actual", formatter: "$", min: -1000, max: 1000 },
  { key: "duration", title: "Duration (In Hours)", formatter: "", min: 0, max: 24 },
  { key: "averageBet", title: "Average Bet", formatter: "$", min: 0, max: 100 },
  { key: "bet", title: "Bet", formatter: "$", min: 0, max: 1000 },
];

// PORT OF `advanced-filters` WITH IN PLAY'S INPUTS (SAVE SHOWN, PLAYER OPTIONS SHOWN, NOT
// PLAYER-ONLY, NO UNHOSTED SWITCH): A `p-toolbar mt-3` WITH "SAVE FILTER" AT THE START AND
// "APPLY" AT THE END (BOTH DISABLED UNTIL SOMETHING IS ON), THE "PLAYER HOST" CARD, THEN THE
// FIVE RANGE CARDS, EACH `mt-3`. "SAVE FILTER" OPENS A `p-confirmPopup` ASKING FOR A NAME.
// `filter` IS THE DRAFT BEING EDITED; APPLY HANDS BACK THE DRAFT WITH ITS CLAUSES BUILT.
export function AdvancedFilters(props: { filter: AdvancedFilter; onChange: (f: AdvancedFilter) => void; onApply: (f: AdvancedFilter) => void }) {
  const { filter, onChange, onApply } = props;
  const toast = useToast();
  const save = useCreateSavedFilter();
  const [saveAnchor, setSaveAnchor] = useState<HTMLElement | null>(null);
  const [name, setName] = useState("");
  const enabled = canApply(filter);

  const accept = () => {
    setSaveAnchor(null);
    save.mutate(
      { name, filter: { ...filter, active: enabled } },
      {
        onSuccess: () => {
          setName("");
          toast("success", "Save Filter", "Your filter was saved successfully.");
        },
        onError: () => toast("error", "Save Filter", "Your filter was not saved, Please try again."),
      },
    );
  };

  return (
    <>
      <div className="p-toolbar mt-3">
        <div className="p-toolbar-group-start">
          <PButton
            label="Save Filter"
            icon="plus"
            className="mr-2"
            hostElement
            disabled={!enabled}
            loading={save.isPending}
            onClick={(e) => setSaveAnchor(e.currentTarget)}
          />
        </div>
        <div className="p-toolbar-group-end">
          <PButton label="Apply" icon="filter" hostElement disabled={!enabled} onClick={() => onApply({ ...withFilters(filter, "advanced"), active: true })} />
        </div>
      </div>

      <div className="mt-3">
        <FilterPlayer title="Player Host" data={filter.player} showOptions showUnhosted={false} onChange={(player) => onChange({ ...filter, player })} />
      </div>

      {RANGES.map((r) => (
        <div key={r.key} className="mt-3">
          <FilterRange
            title={r.title}
            data={filter[r.key]}
            formatter={r.formatter}
            minValue={r.min}
            maxValue={r.max}
            minLabel="Min"
            maxLabel="Max"
            onChange={(data) => onChange({ ...filter, [r.key]: data })}
          />
        </div>
      ))}

      <ConfirmPopup
        anchorEl={saveAnchor}
        ariaLabel="Save Filter"
        acceptLabel="Save"
        rejectLabel="Cancel"
        onHide={() => setSaveAnchor(null)}
        onAccept={accept}
        onReject={() => {
          setName("");
          setSaveAnchor(null);
        }}
      >
        <div className="flex flex-column w-full">
          <label className="font-bold" htmlFor="save-filter-name">
            Name:
          </label>
          <input id="save-filter-name" type="text" className="p-inputtext" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
      </ConfirmPopup>
    </>
  );
}
